import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Receipt, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const EXPENSE_TYPES = ["Maintenance", "Insurance", "Legal & Registration", "Broker Commission", "Utilities", "Property Tax", "Repairs", "Fit-out", "Penalty", "Other"];

const EMPTY = { lease_id: "none", expense_type: "Maintenance", description: "", amount: "", currency: "USD", date: new Date().toISOString().slice(0, 10) };

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [leases, setLeases] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [fType, setFType] = useState("all");

  const load = useCallback(() => {
    const params = {};
    if (fType !== "all") params.expense_type = fType;
    api.get("/expenses", { params }).then((r) => setExpenses(r.data));
  }, [fType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/leases").then((r) => setLeases(r.data));
    api.get("/currencies").then((r) => setCurrencies(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.amount) { toast.error("Amount required"); return; }
    try {
      await api.post("/expenses", {
        ...form,
        lease_id: form.lease_id === "none" ? null : form.lease_id,
        amount: parseFloat(form.amount) || 0,
      });
      toast.success("Expense recorded");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const total = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between fade-up">
        <div>
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-2">One-Time Costs</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Expenses</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm gap-2" data-testid="add-expense-btn"><Plus size={16} /> New Expense</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-card">
            <DialogHeader><DialogTitle className="font-display tracking-tight">New Expense</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label>Expense Type</Label>
                <Select value={form.expense_type} onValueChange={(v) => set("expense_type", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="expense-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXPENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Link to Lease (optional)</Label>
                <Select value={form.lease_id} onValueChange={(v) => set("lease_id", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="expense-lease"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No lease (standalone)</SelectItem>
                    {leases.map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount</Label>
                <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1.5 rounded-sm font-mono" data-testid="expense-amount" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="expense-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {currencies.map((cc) => <SelectItem key={cc.code} value={cc.code}>{cc.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5 rounded-sm font-mono" data-testid="expense-date" />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-sm" data-testid="expense-description" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="rounded-sm" data-testid="save-expense-btn">Record Expense</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-4 fade-up">
        <div className="w-56">
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="rounded-sm" data-testid="filter-expense-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All expense types</SelectItem>
              {EXPENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-muted-foreground">Total: <span className="font-mono font-semibold text-foreground" data-testid="expenses-total">{money(total)}</span></div>
      </div>

      {expenses.length === 0 ? (
        <Card className="rounded-sm border-dashed border-border shadow-none p-16 text-center bg-card fade-up">
          <Receipt size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm">No expenses recorded. Track one-time costs beyond rent here.</p>
        </Card>
      ) : (
        <Card className="rounded-sm border-border shadow-none overflow-hidden bg-card fade-up">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Lease</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((e) => (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell><Badge variant="secondary" className="rounded-sm">{e.expense_type}</Badge></TableCell>
                    <TableCell>{e.description || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{e.lease_title || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{e.date}</TableCell>
                    <TableCell className="text-right font-mono">{money(e.amount, e.currency)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => del(e.id)} data-testid={`delete-expense-${e.id}`}
                        className="h-7 w-7 text-muted-foreground hover:text-destructive rounded-sm"><Trash size={15} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
